import { useEffect, useState } from "react";
import stylez from "../StockTable/StockTable.module.css";
import axiosInstance from "../../../Configuration/axiosInstance";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";
import Modal from "../../../Components/Shared/Modal/Modal";

const EditStock = ({ show, stockId, setShow }) => {
  const userEmail = sessionStorage.getItem("email");
  const [stocks, setStocks] = useState();
  const [payLoad, setPayLoad] = useState([]);
  const [msg, setMsg] = useState("");
  const [comments, setComments] = useState("");
  const [dataChanged, setDataChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // ✅ NEW: Progress tracking states
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [processingMessage, setProcessingMessage] = useState("");
  const [showProgress, setShowProgress] = useState(false);

  useEffect(() => {
    axiosInstance.get(`stockRoute/${stockId}`).then((res) => {
      console.log(res.data.data);
      setStocks(res.data.data);
      setLoading(false);
    });
  }, [dataChanged]);

  const handleChange = (event, index) => {
    let { value } = event.target;
    value = Math.trunc(value);
    setPayLoad((prevValue) => {
      const updatedPayload = [...prevValue];
      const foundIndex = updatedPayload.findIndex((obj) => obj.warehouse === index);

      if (foundIndex !== -1) {
        updatedPayload[foundIndex].quantity = value;
      } else {
        updatedPayload.push({ warehouse: index, quantity: value });
      }

      return updatedPayload;
    });
    setMsg("");
  };

  // ✅ NEW: Progress display component
  const ProgressDisplay = () => (
    <div className={stylez.progressContainer}>
      <div className={stylez.progressHeader}>
        <h3>🔄 Processing Stock Update</h3>
        <p>Please wait while we update your stock and sync with WooCommerce...</p>
      </div>
      
      <div className={stylez.progressSteps}>
        {progressSteps.map((step, index) => (
          <div 
            key={index} 
            className={`${stylez.progressStep} ${index <= currentStep ? stylez.completed : stylez.pending}`}
          >
            <div className={stylez.stepNumber}>
              {index <= currentStep ? "✅" : index === currentStep + 1 ? "⏳" : "⏸️"}
            </div>
            <div className={stylez.stepContent}>
              <div className={stylez.stepMessage}>{step.message}</div>
              {step.timestamp && (
                <div className={stylez.stepTime}>
                  {new Date(step.timestamp).toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {processingMessage && (
        <div className={stylez.currentProcessing}>
          <div className={stylez.processingSpinner}>⏳</div>
          <span>{processingMessage}</span>
        </div>
      )}
    </div>
  );

  const handleEditSave = async (action) => {
    let id = stocks._id;
    
    // ✅ Existing validation
    if (comments.length === 0 || comments.length === 1) {
      setMsg("Reason is a required field");
      return;
    }
    
    const modifiedComment = `Stock updated by: ${userEmail}. Reason: ${comments}`;
    
    if (payLoad.length === 0) {
      setMsg("Fill in the input field");
      return;
    }
    
    if (payLoad[0]?.quantity <= 0 || payLoad[1]?.quantity <= 0 || payLoad[2]?.quantity <= 0) {
      setMsg("Input amount cannot be zero of or less than zero(negative)");
      return;
    }

    // Check if the payload quantity exceeds the stock amount in the warehouse
    if (action === "delete") {
      let isExceedingStock = false;
      stocks.warehouses.forEach((warehouse) => {
        const payloadQuantity = payLoad.find((obj) => obj.warehouse === warehouse.warehouse._id)?.quantity;
        if (payloadQuantity > warehouse.quantity) {
          isExceedingStock = true;
        }
      });

      if (isExceedingStock) {
        setMsg("Entered amount exceeds Stock Amount");
        return;
      }
    }

    // ✅ NEW: Start progress tracking
    setIsProcessing(true);
    setShowProgress(true);
    setLoading(true);
    setMsg("");
    
    // Initialize progress steps
    const initialSteps = [
      { message: "🔍 Validating stock update...", timestamp: new Date() },
      { message: "📦 Processing warehouse quantities...", timestamp: null },
      { message: "💾 Saving to database...", timestamp: null },
      { message: "🌐 Syncing with WooCommerce...", timestamp: null },
      { message: "✅ Update completed!", timestamp: null }
    ];
    
    setProgressSteps(initialSteps);
    setCurrentStep(0);
    setProcessingMessage("Starting stock update process...");

    try {
      // Simulate step progression
      setTimeout(() => {
        setCurrentStep(1);
        setProcessingMessage("Updating warehouse quantities...");
      }, 500);

      setTimeout(() => {
        setCurrentStep(2);
        setProcessingMessage("Saving changes to database...");
      }, 1000);

      setTimeout(() => {
        setCurrentStep(3);
        setProcessingMessage("Syncing stock levels with WooCommerce website...");
      }, 1500);

      const response = await axiosInstance.patch(`stockRoute/updateStock/${id}`, {
        payLoad,
        action,
        comments: modifiedComment,
      });

      // ✅ NEW: Handle progress updates from backend
      if (response.data.progressUpdates) {
        setProgressSteps(response.data.progressUpdates);
        setCurrentStep(response.data.progressUpdates.length - 1);
      }

      // ✅ NEW: Show completion message with details
      const { stockChange, wooCommerceSync, processingTime } = response.data;
      
      let completionMessage = `${response.data.message}\n\n`;
      completionMessage += `📊 Stock Change: ${stockChange.from} → ${stockChange.to} (${stockChange.difference >= 0 ? '+' : ''}${stockChange.difference})\n`;
      
      if (wooCommerceSync) {
        if (wooCommerceSync.success) {
          completionMessage += `🌐 WooCommerce: ${wooCommerceSync.message}\n`;
        } else {
          completionMessage += `⚠️ WooCommerce sync failed: ${wooCommerceSync.error}\n`;
        }
      }
      
      if (processingTime) {
        completionMessage += `⏱️ Processing time: ${processingTime.totalTime}`;
      }

      setMsg(completionMessage);
      setProcessingMessage("Stock update completed successfully!");

      // Close modal after successful update
      setTimeout(() => {
        setShow(false);
        setShowProgress(false);
        setIsProcessing(false);
        setProgressSteps([]);
        setCurrentStep(0);
      }, 3000);

      setDataChanged(!dataChanged);

    } catch (error) {
      setIsProcessing(false);
      setShowProgress(false);
      setProgressSteps([]);
      setCurrentStep(0);
      setLoading(false);
      
      let errorMessage = "Error updating stock. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMsg(errorMessage);
      console.error("Error updating stock:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show}>
      <div className={stylez.editModal}>
        {/* ✅ NEW: Show progress screen when processing */}
        {showProgress ? (
          <ProgressDisplay />
        ) : loading ? (
          <div>
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className={stylez.modalHeading}>
              <h2>Edit Stock</h2>
              <span
                onClick={() => {
                  if (!isProcessing) {
                    setShow(false);
                  }
                }}
                className={`${stylez.closeModal} ${isProcessing ? stylez.disabled : ''}`}
              >
                &times;
              </span>
            </div>
            <h3>
              {stocks?.product?.productName}-{stocks?.product?.sku}
            </h3>
            <div>
              <table className={stylez.stockTable}>
                <thead>
                  {stocks?.warehouses?.map((i) => (
                    <th key={i.warehouse._id}>{i?.warehouse?.name}</th>
                  ))}
                </thead>
                <tbody>
                  <tr>
                    {stocks?.warehouses?.map((warehouse, index) => (
                      <td key={index}>{warehouse?.quantity}</td>
                    ))}
                  </tr>
                  <tr>
                    {stocks?.warehouses?.map((warehouse, index) => (
                      <td key={index}>
                        <input
                          name="quantity"
                          min="0"
                          type="number"
                          disabled={isProcessing}
                          onChange={(e) => {
                            handleChange(e, warehouse.warehouse._id);
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ display: "Flex", flexDirection: "column" }}>
              <label>Reason for Stock Update:</label>
              <textarea
                style={{ width: "40vw", height: "15vh" }}
                type="text"
                placeholder="Reason for this stock update"
                required
                disabled={isProcessing}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
            <div className={stylez.btnDivs}>
              <button
                className={stylez.updtStckBtn}
                disabled={isProcessing}
                onClick={() => {
                  let action = "add";
                  return handleEditSave(action);
                }}
              >
                {isProcessing ? "Processing..." : "Add to Stock"}
              </button>
              <button
                className={stylez.deletebtn}
                disabled={isProcessing}
                onClick={() => {
                  let action = "delete";
                  return handleEditSave(action);
                }}
              >
                {isProcessing ? "Processing..." : "Delete from Stock"}
              </button>
            </div>
            <div className={stylez.error}>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{msg}</pre>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default EditStock;
